import { apiProblemText } from "../../api/api-error.js";
import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * The Developers panel: API keys, and what is true of webhooks today (review #187). Rebuilt by plan-014
 * unit C3 (design §27.15.1 `Settings` changed; FR12-SEC-07, FR12-SCR-07).
 *
 * **A KEY IS SHOWN EXACTLY ONCE.** It exists in the create (or rotate) response and in the dialog below,
 * and nowhere else — not in a query cache that outlives the dialog, not in a log, not in a second render.
 * The issued value is held in component state until the dialog closes and then discarded; there is no
 * `useQuery` for it, because a query cache is a place a secret would survive.
 *
 * **WHAT A KEY CARRIES NOW (plan-014 C3).** An explicit scope — this workspace, or the organization's
 * finance reads — its scopes, an end date (required), optional networks it may be used from, and an owner:
 * the person who made it (it never does more than they can, and stops when they leave) or, for the
 * organization's Owners and Admins, an organization integration that outlives any one person. Rotating a
 * key issues a new value and revokes the old one at once.
 *
 * **WHAT THE DESIGN ASKS FOR THAT THE DATA CANNOT GIVE.** The prototype renders a key as
 * `wtk_live_••••••••••••3f2a`. `api_key` stores the fixed public prefix and a SHA-256 digest; there is no
 * per-key tail, and adding one would mean storing part of the secret. The table therefore shows the key's
 * **name** and its prefix, which is what the platform knows.
 *
 * **WHO IS OFFERED WHAT** comes from the capability module, never a role compared here: workspace keys to
 * `api_key.manage` (Owner, Admin) — and, limited to the integration scopes, to `api_key.integration_manage`
 * (Developer); organization keys and integration keys to the organization's `account.api_key.manage`. The
 * API decides in the end (and refuses a scope the person does not hold themselves, `scope_insufficient`).
 *
 * Webhooks (customer-portal review #187): destinations are added through the API, no signing secret is
 * issued yet and WeTalk never sends an unsigned delivery — so the block says that, and lists what is
 * registered.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SCOPE_DESCRIPTION } from "@wetalk/api-client/route-fact";
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Mono,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
  Textarea,
} from "@wetalk/ui";
import { API_SCOPE, holdsAccountCapability, holdsWorkspaceCapability, type ApiScope } from "@wetalk/domain";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { authSessionQuery, webhookEndpointQuery } from "../../api/query.js";
import { useMemberCan } from "../../shell/member-capability.js";
import type { ApiKeyOwnerKindView, ApiKeyRecordView, IssuedApiKeyRecordView } from "../../api/slice/api-key.js";

import "./settings.css";
import "../billing/billing.css";

/**
 * What the webhook block says (review #187): only what is true today. No signing secret is issued
 * yet and WeTalk never sends an unsigned delivery, so nothing reaches a destination; a destination
 * can be added only through the API.
 */
export const WEBHOOK_NOT_SENT =
  portalText("core", "settings.webhooks_are_not_delivered_yet_every_delivery_will_be_signed_so_y_e3caaf") +
  portalText("core", "settings.from_wetalk_and_signing_secrets_are_not_issued_yet_wetalk_never_s_e23569") +
  portalText("core", "settings.signed_deliveries_with_the_secret_shown_to_you_once_are_coming_b8a9ff");
export function webhookNotSent(): typeof WEBHOOK_NOT_SENT { return portalText("core", "settings.webhooks_are_not_delivered_yet_every_delivery_will_be_signed_so_y_e3caaf") +
  portalText("core", "settings.from_wetalk_and_signing_secrets_are_not_issued_yet_wetalk_never_s_e23569") +
  portalText("core", "settings.signed_deliveries_with_the_secret_shown_to_you_once_are_coming_b8a9ff"); }

export const WEBHOOK_NO_DESTINATION =
  portalText("core", "settings.destinations_are_added_through_the_api_for_now_post_v1_webhook_en_67fa00");
export function webhookNoDestination(): typeof WEBHOOK_NO_DESTINATION { return portalText("core", "settings.destinations_are_added_through_the_api_for_now_post_v1_webhook_en_67fa00"); }

export const WEBHOOK_REGISTERED_DESTINATION =
  portalText("core", "settings.destinations_are_added_through_the_api_for_now_these_are_register_ddfffe");
export function webhookRegisteredDestination(): typeof WEBHOOK_REGISTERED_DESTINATION { return portalText("core", "settings.destinations_are_added_through_the_api_for_now_these_are_register_ddfffe"); }


/** The API's own limit on a key's name (`API_KEY_NAME_MAX_LENGTH`, review item 191). */
const API_KEY_NAME_MAX_LENGTH = 60;

/**
 * What each scope lets a key do, in the words a business owner reads (review item 193). One source,
 * shared with the public API reference's scope table (review item 188).
 */
export { SCOPE_DESCRIPTION };

/** The scopes an organization key holds (design §27.5.4). */
export const ACCOUNT_KEY_SCOPE = ["billing:read", "usage:read", "document:read"] as const;
type AccountKeyScope = (typeof ACCOUNT_KEY_SCOPE)[number];
type KeyScope = ApiScope | AccountKeyScope;

/** The scopes a Developer (`api_key.integration_manage` alone) may put on a key (design §27.5.3). */
export const INTEGRATION_KEY_SCOPE: readonly ApiScope[] = ["agent:read", "agent:write", "conversation:read"];

/** The resource an API scope names, as the table's one tag says it. */
const SCOPE_RESOURCE_LABEL: ReadonlyMap<string, string> = new Map([
  ["agent", portalText("core", "dashboard.agents_64acf7")],
  ["conversation", portalText("core", "settings.conversations_07c59b")],
  ["recording", portalText("core", "settings.recordings_38adb0")],
  ["campaign", portalText("core", "workspaces.campaigns_01a23a")],
  ["credit", portalText("core", "settings.credit_8a45d3")],
  ["order", portalText("core", "settings.orders_cded09")],
  ["billing", portalText("core", "account_admin.billing_abaec4")],
  ["usage", portalText("core", "settings.usage_0bb186")],
  ["document", portalText("core", "settings.documents_687c82")],
]);
export function scopeResourceLabels(): typeof SCOPE_RESOURCE_LABEL { return new Map([
  ["agent", portalText("core", "dashboard.agents_64acf7")],
  ["conversation", portalText("core", "settings.conversations_07c59b")],
  ["recording", portalText("core", "settings.recordings_38adb0")],
  ["campaign", portalText("core", "workspaces.campaigns_01a23a")],
  ["credit", portalText("core", "settings.credit_8a45d3")],
  ["order", portalText("core", "settings.orders_cded09")],
  ["billing", portalText("core", "account_admin.billing_abaec4")],
  ["usage", portalText("core", "settings.usage_0bb186")],
  ["document", portalText("core", "settings.documents_687c82")],
]); }


/**
 * The design's one scope tag per key ("Read only", "Conversations", "Full access"), derived from
 * the key's real scopes, which the tag's `title` lists in full. Every scope → "Full access"; one
 * resource → that resource and what it may do to it ("Agents · read", "Agents · read & write",
 * so a reading key and a writing key never look alike — review item 192); several resources, all
 * read → "Read only"; otherwise the resources.
 */
export function scopeSummary(scope: readonly string[]): {
  readonly label: string;
  readonly tone: "neutral" | "accent" | "accent-2";
} {
  if (API_SCOPE.every((each: ApiScope) => scope.includes(each))) {
    return { label: portalText("core", "settings.full_access_5e4017"), tone: "accent-2" };
  }
  const resource = [...new Set(scope.map((each) => each.split(":")[0] ?? each))];
  if (resource.length === 1) {
    const only = resource[0] ?? "";
    const action = [...new Set(scope.map((each) => each.split(":")[1] ?? ""))].sort().map((one) => one === "read" ? portalText("core", "settings.scope_read") : one === "write" ? portalText("core", "settings.scope_write") : one).join(" & ");
    return { label: portalText("core", "settings._a7e510", { value0: scopeResourceLabels().get(only) ?? only, value1: action }), tone: "accent" };
  }
  if (scope.every((each) => each.endsWith(":read"))) {
    return { label: portalText("core", "settings.read_only_601dcc"), tone: "neutral" };
  }
  return {
    label: resource.map((each) => scopeResourceLabels().get(each) ?? each).join(", "),
    tone: "accent",
  };
}

/** "31 Jan 2027" — an end date as a person reads it, in the browser's own zone. */
export function endDateText(iso: string): string {
  return new Date(iso).toLocaleDateString(portalLocale() === "en" ? "en-GB" : "el-GR", { day: "numeric", month: "short", year: "numeric" });
}

/** A date field's `YYYY-MM-DD` as the end of that day, UTC — the instant the API is sent. */
export function endOfDayIso(day: string): string {
  return `${day}T23:59:59.000Z`;
}

/** Tomorrow as `YYYY-MM-DD`: an end date is in the future. */
function tomorrowOf(now: Date): string {
  return new Date(now.getTime() + 86_400_000).toISOString().slice(0, 10);
}

/** The ranges as typed — one per line or comma-separated — trimmed, blanks dropped. */
export function networkListOf(typed: string): readonly string[] {
  return typed
    .split(/[\n,]/)
    .map((one) => one.trim())
    .filter((one) => one !== "");
}

/** Which keys the signed-in person may manage here, from the capability module. */
export interface KeyAuthority {
  /** `api_key.manage` in this workspace: any workspace scope (bounded by their own). */
  readonly workspaceFull: boolean;
  /** `api_key.integration_manage` in this workspace: the integration scopes. */
  readonly workspaceIntegration: boolean;
  /** `account.api_key.manage`: organization keys and organization integration keys. */
  readonly organization: boolean;
}

export function keyAuthorityOf(role: string | null, accountRole: string | null): KeyAuthority {
  const memberRole = role as Parameters<typeof holdsWorkspaceCapability>[0] | null;
  const organizationRole = accountRole as Parameters<typeof holdsAccountCapability>[0] | null;
  return {
    workspaceFull: memberRole !== null && holdsWorkspaceCapability(memberRole, "api_key.manage"),
    workspaceIntegration: memberRole !== null && holdsWorkspaceCapability(memberRole, "api_key.integration_manage"),
    organization: organizationRole !== null && holdsAccountCapability(organizationRole, "account.api_key.manage"),
  };
}

/** Which create dialog is open, if any. */
type Creating = null | { readonly scopeKind: "workspace" | "account" };

export function ApiKeyPanel({
  workspaceId,
  accountId,
}: {
  readonly workspaceId: string;
  readonly accountId: string;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const member = useMemberCan();
  // Security review M-8: Rotate is offered only on the signed-in person's own keys (and on integration keys).
  const signedInPersonId = useQuery(authSessionQuery(gateway)).data?.person.personId ?? null;
  const authority = keyAuthorityOf(member.role, member.accountRole);
  const managesWorkspaceKeys = authority.workspaceFull || authority.workspaceIntegration;
  const workspaceKey = useQuery({
    queryKey: ["api-key", "workspace", workspaceId] as const,
    queryFn: () => gateway.listWorkspaceApiKeys(workspaceId),
    enabled: managesWorkspaceKeys && workspaceId !== "",
  });
  const accountKey = useQuery({
    queryKey: ["api-key", "account", accountId] as const,
    queryFn: () => gateway.listAccountApiKeys(accountId),
    enabled: authority.organization && accountId !== "",
  });
  const endpoint = useQuery({ ...webhookEndpointQuery(gateway), enabled: managesWorkspaceKeys });

  const [creating, setCreating] = useState<Creating>(null);
  const [issued, setIssued] = useState<IssuedApiKeyRecordView | null>(null);
  const [message, setMessage] = usePortalMessage();
  // A revoke waits here until it is confirmed: it stops every integration using the key.
  const [revoking, setRevoking] = useState<ApiKeyRecordView | null>(null);
  // A rotation waits here for its new end date.
  const [rotating, setRotating] = useState<ApiKeyRecordView | null>(null);
  // Revoked keys stay on record (the audit log names them) but out of the way (review item 191).
  const [showRevoked, setShowRevoked] = useState(false);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["api-key"] });
  };

  const revoke = useMutation({
    mutationFn: (apiKeyId: string) => gateway.revokeApiKeyById(apiKeyId),
    onSuccess: async () => {
      setMessage(null);
      await refresh();
    },
    onError: (error: unknown) => {
      setMessage(() => (apiProblemText(error, portalText("core", "settings.that_key_could_not_be_revoked_31fba5"))));
    },
  });

  if (!managesWorkspaceKeys && !authority.organization) {
    const refusal = member.whyNot("api_key.manage");
    return (
      <div>
        <p className="c-settings-title">{portalText("core", "settings.developers_71348b")}</p>
        <p className="c-settings-dek">{portalText("core", "settings.keys_for_the_api_and_where_webhooks_will_go_55c2db")}</p>
        {refusal === null ? null : <p className="c-note">{refusal}</p>}
      </div>
    );
  }

  const offered = (rows: readonly ApiKeyRecordView[] | undefined): readonly ApiKeyRecordView[] =>
    rows?.filter((one) => showRevoked || !one.revoked) ?? [];
  const revokedCount = [...(workspaceKey.data ?? []), ...(accountKey.data ?? [])].filter((one) => one.revoked).length;

  return (
    <div className="c-settings-developers">
      <p className="c-settings-title">{portalText("core", "settings.developers_71348b")}</p>
      <p className="c-settings-dek">{portalText("core", "settings.keys_for_the_api_and_where_webhooks_will_go_a_key_is_shown_once_w_97a872")}</p>

      {managesWorkspaceKeys ? (
        <KeyTable
          caption={portalText("core", "settings.api_keys_issued_for_this_workspace_995f82")}
          rows={workspaceKey.data === undefined ? undefined : offered(workspaceKey.data)}
          emptyText={(workspaceKey.data ?? []).some((one) => one.revoked) ? portalText("core", "settings.no_live_keys_5698a8") : portalText("core", "settings.no_keys_yet_c1e947")}
          onRotate={setRotating}
          onRevoke={setRevoking}
          signedInPersonId={signedInPersonId}
        />
      ) : null}

      {revokedCount === 0 ? null : (
        <button
          type="button"
          className="btn btn-ghost c-settings-link"
          aria-pressed={showRevoked}
          onClick={() => {
            setShowRevoked((held) => !held);
          }}
        >
          {showRevoked ? portalText("core", "settings.hide_revoked_keys_add3ad") : portalText("core", "settings.show_revoked_keys_58e521", { value0: String(revokedCount) })}
        </button>
      )}

      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}

      <div className="c-settings-actions">
        {managesWorkspaceKeys ? (
          <Button
            variant="secondary"
            onClick={() => {
              setCreating({ scopeKind: "workspace" });
            }}
          >
            <Icon glyph={GLYPH.key} size="md" />{portalText("core", "settings.create_a_key_609f52")}</Button>
        ) : null}
        {/* The public API reference (review item 188): scopes, limits, every operation. */}
        <a href={gateway.apiReferenceUrl()} className="btn btn-ghost c-settings-link" target="_blank" rel="noopener">{portalText("core", "settings.read_the_api_docs_281610")}</a>
      </div>

      {/* --- organization keys: the organization's Owners and Admins (account.api_key.manage) ---- */}
      {authority.organization ? (
        <div className="c-settings-orgkeys">
          <p className="c-settings-subtitle">{portalText("core", "settings.organization_keys_12c475")}</p>
          <p className="c-settings-dek">{portalText("core", "settings.read_the_organization_s_balances_usage_and_invoices_from_your_own_258662")}</p>
          <KeyTable
            caption={portalText("core", "settings.organization_api_keys_92de88")}
            rows={accountKey.data === undefined ? undefined : offered(accountKey.data)}
            emptyText={portalText("core", "settings.no_organization_keys_yet_3ff5e9")}
            onRotate={setRotating}
            onRevoke={setRevoking}
            signedInPersonId={signedInPersonId}
          />
          <div className="c-settings-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setCreating({ scopeKind: "account" });
              }}
            >
              <Icon glyph={GLYPH.key} size="md" />{portalText("core", "settings.create_an_organization_key_df192f")}</Button>
          </div>
        </div>
      ) : null}

      {/* --- webhooks: what is true today (review #187) ------------------------------- */}
      {!managesWorkspaceKeys ? null : endpoint.data === undefined ? (
        <p className="c-note">{portalText("core", "settings.reading_your_webhook_destinations_e0032c")}</p>
      ) : (
        <div className="c-settings-webhook">
          <p className="c-settings-webhook-title">{portalText("core", "settings.webhooks_fdfe2d")}</p>
          <p className="c-settings-webhook-note">{webhookNotSent()}</p>
          <p className="c-settings-webhook-note">
            {endpoint.data.length === 0 ? webhookNoDestination() : webhookRegisteredDestination()}
          </p>
          {endpoint.data.length === 0 ? null : (
            <ul className="c-settings-webhook-destination">
              {endpoint.data.map((one) => (
                <li key={one.webhookEndpointId}>
                  <span className="c-settings-webhook-url">{one.url}</span>
                  <span className="c-settings-webhook-event">
                    {one.eventKind.join(", ")}
                    {one.enabled ? "" : portalText("core", "settings._paused_31d650")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <CreateKeyDialog
        creating={creating}
        authority={authority}
        onClose={() => {
          setCreating(null);
        }}
        onIssued={async (made) => {
          setIssued(made);
          setCreating(null);
          setMessage(null);
          await refresh();
        }}
        workspaceId={workspaceId}
        accountId={accountId}
      />

      <RotateKeyDialog
        rotating={rotating}
        onClose={() => {
          setRotating(null);
        }}
        onIssued={async (made) => {
          setIssued(made);
          setRotating(null);
          await refresh();
        }}
      />

      {/* --- revoke ---------------------------------------------------------------------- */}
      <Dialog
        open={revoking !== null}
        title={revoking === null ? portalText("core", "settings.revoke_this_key_c7fdd8") : portalText("core", "settings.revoke_68e86a", { value0: revoking.name })}
        onClose={() => {
          setRevoking(null);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setRevoking(null);
              }}
            >{portalText("core", "workspaces.cancel_77dfd2")}</Button>
            <Button
              variant="primary"
              disabled={revoke.isPending}
              onClick={() => {
                if (revoking !== null) revoke.mutate(revoking.apiKeyId);
                setRevoking(null);
              }}
            >{portalText("core", "settings.revoke_it_53f2a7")}</Button>
          </>
        }
      >
        <p className="c-note">{portalText("core", "settings.anything_using_this_key_stops_working_immediately_and_the_key_can_cbc9c6")}</p>
      </Dialog>

      <IssuedKeyDialog
        issued={issued}
        onClose={() => {
          setIssued(null);
        }}
      />
    </div>
  );
}

/** One table of keys — the prototype's five columns, with owner, networks and end date in their cells. */
function KeyTable({
  caption,
  rows,
  emptyText,
  onRotate,
  onRevoke,
  signedInPersonId,
}: {
  readonly caption: string;
  readonly rows: readonly ApiKeyRecordView[] | undefined;
  readonly emptyText: string;
  readonly onRotate: (key: ApiKeyRecordView) => void;
  readonly onRevoke: (key: ApiKeyRecordView) => void;
  /** Who is signed in: a person-owned key is rotated by that person alone (security review M-8). */
  readonly signedInPersonId: string | null;
}): ReactNode {
  usePortalTranslation("core");
  if (rows === undefined) return <p className="c-note">{portalText("core", "settings.reading_your_keys_77c102")}</p>;
  if (rows.length === 0) return <p className="c-note">{emptyText}</p>;
  return (
    <div className="c-settings-scroll c-settings-keys">
      <Table caption={caption}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{portalText("core", "workspaces.name_709a23")}</TableHeaderCell>
            <TableHeaderCell>{portalText("core", "settings.key_c67dd2")}</TableHeaderCell>
            <TableHeaderCell>{portalText("core", "settings.scope_4651a3")}</TableHeaderCell>
            <TableHeaderCell>{portalText("core", "settings.last_used_f1109d")}</TableHeaderCell>
            <TableHeaderCell>{""}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((one) => (
            <TableRow key={one.apiKeyId}>
              <TableCell>
                <span className="c-settings-key-name">{one.name}</span>
                {/* When and by whom, and whose access bounds it, in one cell (review items 191, 192). */}
                <span className="c-settings-created-by">
                  {one.createdText}{" "}{portalText("core", "settings._by_bb37da")}{" "}{one.createdByName ?? portalText("core", "settings.someone_no_longer_here_f80af0")}
                </span>
                <span className="c-settings-created-by">
                  {one.ownerKind === "account_integration"
                    ? portalText("core", "settings.organization_integration_ce6869")
                    : portalText("core", "settings.acts_for_9b310b", { value0: one.ownerName ?? portalText("core", "settings.someone_no_longer_here_f80af0") })}
                </span>
              </TableCell>
              <TableCell font="mono">
                <span className="c-settings-key">{one.keyPrefix}··········</span>
              </TableCell>
              <TableCell>
                <span className={`tag tag-${scopeSummary(one.scope).tone} c-settings-scope`} title={one.scope.join(" · ")}>
                  {scopeSummary(one.scope).label}
                </span>
                <span className="c-settings-created-by">
                  {one.allowedNetwork.length === 0 ? portalText("core", "settings.any_network_353e91") : one.allowedNetwork.join(", ")}
                </span>
              </TableCell>
              <TableCell>
                <span className="c-settings-used">{one.lastUsedText ?? portalText("core", "settings.never")}</span>
                <span className="c-settings-created-by">
                  {one.expiresAt === null
                    ? portalText("core", "settings.no_end_date_58fb55")
                    : portalText("core", "account_admin._843a2d", { value0: one.expired ? portalText("core", "settings.ended_90303d") : portalText("core", "settings.ends_91856d"), value1: endDateText(one.expiresAt) })}
                </span>
              </TableCell>
              <TableCell>
                {one.revoked ? (
                  <Tag status="critical">{portalText("core", "account_admin.revoked_85f17a")}</Tag>
                ) : (
                  <span className="c-settings-key-act">
                    {/* Security review M-8: a person's key is rotated by that person alone; anyone else revokes it. */}
                    {one.ownerKind === "account_integration" || (signedInPersonId !== null && one.ownerPersonId === signedInPersonId) ? (
                      <button
                        type="button"
                        className="c-settings-revoke"
                        aria-label={portalText("core", "settings.rotate_b1a67a", { value0: one.name })}
                        onClick={() => {
                          onRotate(one);
                        }}
                      >{portalText("core", "settings.rotate_87b85d")}</button>
                    ) : null}
                    <button
                      type="button"
                      className="c-settings-revoke"
                      onClick={() => {
                        onRevoke(one);
                      }}
                    >{portalText("core", "account_admin.revoke_0be720")}</button>
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** The create form: name, scopes, end date, networks and — for the organization's Owners and Admins — owner. */
function CreateKeyDialog({
  creating,
  authority,
  onClose,
  onIssued,
  workspaceId,
  accountId,
}: {
  readonly creating: Creating;
  readonly authority: KeyAuthority;
  readonly onClose: () => void;
  readonly onIssued: (made: IssuedApiKeyRecordView) => Promise<void>;
  readonly workspaceId: string;
  readonly accountId: string;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const scopeKind = creating?.scopeKind ?? "workspace";
  const offered: readonly KeyScope[] =
    scopeKind === "account" ? ACCOUNT_KEY_SCOPE : authority.workspaceFull ? API_SCOPE : INTEGRATION_KEY_SCOPE;
  const [name, setName] = useState("");
  const [scope, setScope] = useState<readonly KeyScope[]>([]);
  const [endDay, setEndDay] = useState("");
  const [network, setNetwork] = useState("");
  const [ownerKind, setOwnerKind] = useState<ApiKeyOwnerKindView>("person");
  const [createMessage, setCreateMessage] = usePortalMessage();
  // The scopes start at one sensible reading scope of the kind, and reset when the kind changes.
  const [shownKind, setShownKind] = useState<"workspace" | "account" | null>(null);
  if (creating !== null && shownKind !== creating.scopeKind) {
    setShownKind(creating.scopeKind);
    setScope(creating.scopeKind === "account" ? ["billing:read"] : ["conversation:read"]);
    setOwnerKind("person");
    setCreateMessage(null);
  }
  const offersIntegration = scopeKind === "account" ? authority.organization : authority.organization && authority.workspaceFull;

  const create = useMutation({
    mutationFn: () => {
      const draft = {
        name,
        scope,
        expiresAt: endOfDayIso(endDay),
        allowedNetwork: networkListOf(network),
        ownerKind: offersIntegration ? ownerKind : "person",
      } as const;
      return scopeKind === "account" ? gateway.createAccountApiKey(accountId, draft) : gateway.createWorkspaceApiKey(workspaceId, draft);
    },
    onSuccess: async (made) => {
      setName("");
      setEndDay("");
      setNetwork("");
      setShownKind(null);
      setCreateMessage(null);
      await onIssued(made);
    },
    onError: (error: unknown) => {
      setCreateMessage(() => (apiProblemText(error, portalText("core", "settings.that_key_could_not_be_created_00000a"))));
    },
  });

  return (
    <Dialog
      open={creating !== null}
      title={scopeKind === "account" ? portalText("core", "settings.create_an_organization_key_df192f") : portalText("core", "settings.create_an_api_key_00b095")}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("core", "workspaces.cancel_77dfd2")}</Button>
          <Button
            variant="primary"
            disabled={name.trim().length === 0 || scope.length === 0 || endDay === "" || create.isPending}
            onClick={() => {
              create.mutate();
            }}
          >{portalText("core", "settings.create_the_key_060368")}</Button>
        </>
      }
    >
      <Field
        label={portalText("core", "workspaces.name_709a23")}
        description={portalText("core", "settings.what_this_key_is_for_up_to_characters_it_is_the_only_thing_that_t_aa6761", { value0: String(API_KEY_NAME_MAX_LENGTH) })}
      >
        {(control) => (
          <Input
            {...control}
            value={name}
            maxLength={API_KEY_NAME_MAX_LENGTH}
            onChange={(event) => {
              setName(event.currentTarget.value);
              setCreateMessage(null);
            }}
          />
        )}
      </Field>
      <fieldset className="c-settings-scopes">
        <legend className="c-setting-title">{portalText("core", "settings.what_it_may_do_73791a")}</legend>
        <p className="c-setting-dek">{portalText("core", "settings.a_key_is_judged_by_its_scopes_and_never_does_more_than_you_can_yo_15df0a")}</p>
        {offered.map((each) => (
          <label key={each} className="c-switch c-settings-scope-option">
            <input
              type="checkbox"
              checked={scope.includes(each)}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setScope((held) => (checked ? [...held, each] : held.filter((one) => one !== each)));
              }}
            />
            <span className="c-switch-track" aria-hidden="true">
              <span className="c-switch-knob" />
            </span>
            <span className="c-settings-scope-text">
              <span className="c-settings-scope-description">{SCOPE_DESCRIPTION[each]}</span>
              <span className="c-settings-scope-id">{each}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {scope.length === 0 ? (
        <p className="c-note">{portalText("core", "settings.pick_at_least_one_thing_the_key_may_do_a_key_with_none_can_do_not_6b52c5")}</p>
      ) : null}
      <Field label={portalText("core", "settings.ends_on_5c262f")} description={portalText("core", "settings.every_key_has_an_end_date_it_stops_working_at_the_end_of_that_day_5cada8")}>
        {(control) => (
          <Input
            {...control}
            type="date"
            value={endDay}
            min={tomorrowOf(new Date())}
            onChange={(event) => {
              setEndDay(event.currentTarget.value);
              setCreateMessage(null);
            }}
          />
        )}
      </Field>
      <Field
        label={portalText("core", "settings.allowed_networks_optional_eb9a6a")}
        description={portalText("core", "settings.addresses_or_ranges_this_key_may_be_used_from_one_per_line_203_0_fbd2bc")}
      >
        {(control) => (
          <Textarea
            {...control}
            rows={2}
            value={network}
            onChange={(event) => {
              setNetwork(event.currentTarget.value);
              setCreateMessage(null);
            }}
          />
        )}
      </Field>
      {offersIntegration ? (
        <fieldset className="c-settings-scopes">
          <legend className="c-setting-title">{portalText("core", "settings.who_it_belongs_to_ff34dc")}</legend>
          <Radio
            name="api-key-owner"
            label={portalText("core", "settings.me_it_stops_working_if_i_leave_and_never_does_more_than_i_can_381a8a")}
            checked={ownerKind === "person"}
            onChange={() => {
              setOwnerKind("person");
            }}
          />
          <Radio
            name="api-key-owner"
            label={portalText("core", "settings.the_organization_an_integration_the_owners_and_admins_manage_a3c22e")}
            checked={ownerKind === "account_integration"}
            onChange={() => {
              setOwnerKind("account_integration");
            }}
          />
        </fieldset>
      ) : null}
      {createMessage === null ? null : <p className="c-note c-note-critical">{createMessage}</p>}
    </Dialog>
  );
}

/** Rotating asks for the new end date, then shows the new key once; the old one stops at that moment. */
function RotateKeyDialog({
  rotating,
  onClose,
  onIssued,
}: {
  readonly rotating: ApiKeyRecordView | null;
  readonly onClose: () => void;
  readonly onIssued: (made: IssuedApiKeyRecordView) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const [endDay, setEndDay] = useState("");
  const [rotateMessage, setRotateMessage] = usePortalMessage();
  const rotate = useMutation({
    mutationFn: (apiKeyId: string) => gateway.rotateApiKey(apiKeyId, endOfDayIso(endDay)),
    onSuccess: async (made) => {
      setEndDay("");
      setRotateMessage(null);
      await onIssued(made);
    },
    onError: (error: unknown) => {
      setRotateMessage(() => (apiProblemText(error, portalText("core", "settings.that_key_could_not_be_rotated_b338cd"))));
    },
  });
  return (
    <Dialog
      open={rotating !== null}
      title={rotating === null ? portalText("core", "settings.rotate_this_key_bae568") : portalText("core", "settings.rotate_378067", { value0: rotating.name })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("core", "workspaces.cancel_77dfd2")}</Button>
          <Button
            variant="primary"
            disabled={endDay === "" || rotate.isPending}
            onClick={() => {
              if (rotating !== null) rotate.mutate(rotating.apiKeyId);
            }}
          >{portalText("core", "settings.rotate_it_e8a78c")}</Button>
        </>
      }
    >
      <p className="c-note">{portalText("core", "settings.a_new_key_with_the_same_name_scopes_and_networks_is_made_and_show_3fe8ca")}</p>
      <Field label={portalText("core", "settings.new_key_ends_on_c9e099")}>
        {(control) => (
          <Input
            {...control}
            type="date"
            value={endDay}
            min={tomorrowOf(new Date())}
            onChange={(event) => {
              setEndDay(event.currentTarget.value);
              setRotateMessage(null);
            }}
          />
        )}
      </Field>
      {rotateMessage === null ? null : <p className="c-note c-note-critical">{rotateMessage}</p>}
    </Dialog>
  );
}

/** The one dialog whose content cannot be recovered. It says so, and it says so first. */
function IssuedKeyDialog({
  issued,
  onClose,
}: {
  readonly issued: IssuedApiKeyRecordView | null;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  const close = (): void => {
    setCopied(null);
    onClose();
  };
  return (
    <Dialog
      open={issued !== null}
      title={portalText("core", "settings.copy_this_key_now_00ec57")}
      onClose={close}
      // Escape must not lose a live key that can never be shown again (review item 189): only
      // the button below closes this dialog.
      dismissible={false}
      actions={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              if (issued === null) return;
              navigator.clipboard.writeText(issued.key).then(
                () => {
                  setCopied("yes");
                },
                () => {
                  setCopied("failed");
                },
              );
            }}
          >
            {copied === "yes" ? portalText("core", "workspaces.copied_8e3df4") : portalText("core", "settings.copy_the_key_39963a")}
          </Button>
          <Button variant="primary" onClick={close}>{portalText("core", "settings.i_have_copied_it_f8ece1")}</Button>
        </>
      }
    >
      {issued === null ? null : (
        <div className="c-once">
          <span className="c-setting-title">{portalText("core", "settings.this_is_the_only_time_a56415")}{" "}{issued.name}{" "}{portalText("core", "settings.will_be_shown_7a1f96")}</span>
          <span className="c-once-value">
            <Mono wrap>{issued.key}</Mono>
          </span>
          <span className="c-setting-dek">{portalText("core", "settings.we_keep_a_one_way_digest_of_it_and_nothing_else_so_it_cannot_be_s_bbbb99")}</span>
          {copied === "failed" ? (
            <span className="c-note c-note-critical">{portalText("core", "settings.this_browser_would_not_copy_it_select_the_key_above_and_copy_it_y_057c15")}</span>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
